/*
  Месяцы с начислениями из двух источников - блок вкладки «Файлы» (решение
  владельца 01.10.2026, «выбор человека на вкладке»).

  По счёту за месяц в портфеле уже есть начисления (например, из обмена с 1С),
  а файл УК называет услуги иначе. Записанные рядом, они удвоили бы долг
  месяца, поэтому перенос их не писал. Здесь человек видит записанное рядом со
  строками файла и решает по отмеченным месяцам:

  - «Оставить записанные» - портфель не меняется;
  - «Заменить начислениями файла» - только через окно подтверждения: записанные
    начисления месяца удаляются, и это не отменить. Выключена, если среди
    отмеченных есть месяц, который заменить нельзя (причина - в строке);
  - «Дописать к записанным» - когда в файле настоящая другая услуга.

  Устроено по образцу расхождений с портфелем (`Divergences.jsx`): отметки и
  кнопки - только на показанных строках, на узком экране над таблицей видимая
  кнопка «отметить показанные». Заранее не отмечено ничего. Пока идёт перенос,
  кнопки выключены: сервер всё равно отказал бы (409).
*/

import { useState } from 'react'

import { Lock } from '../AccessContext.jsx'
import { Check, Confirm, Notice } from '../Common.jsx'
import MonthRow from './MonthRow.jsx'
import TransferLock from './TransferLock.jsx'
import useMonthChoices, { PAGE } from './useMonthChoices.js'

export const BLOCKED_HINT =
  'Среди отмеченных есть месяцы, которые заменить нельзя, - причина указана в строке. Снимите с них отметку или выберите другое действие.'

export default function MonthChoices({ revision, onDecided, locked = false }) {
  const months = useMonthChoices(revision, onDecided)
  const [confirming, setConfirming] = useState(false)

  if (months.loadError) return <Notice error={months.loadError} />
  if (!months.items.length) return null

  const { items, visible, chosen, blocked, busy, error } = months
  const count = chosen.length
  const allChosen = count === visible.length
  const shownTitle = `Отметить показанные месяцы (${visible.length})`
  const hidden = items.length - visible.length
  const idle = busy || locked || count === 0

  const closeConfirm = () => {
    setConfirming(false)
    months.forgetError()
  }
  const replace = async () => {
    if (await months.decide('replace')) setConfirming(false)
  }

  return (
    <section className="section">
      <h2 className="section__title">Месяцы с начислениями из двух источников · {items.length}</h2>
      <p className="muted">
        За эти месяцы в портфеле уже есть начисления, а файл называет услуги иначе. Записанные рядом,
        они удвоили бы долг месяца, поэтому строки файла не записаны. Сравните и выберите, что сделать с
        отмеченными месяцами.
      </p>
      <div className="card">
        <div className="row divergence__all">
          <button type="button" className="button button--quiet" onClick={() => months.chooseShown(!allChosen)}>
            {allChosen ? `Снять отметки с показанных (${visible.length})` : shownTitle}
          </button>
        </div>
        <div className="table-scroll">
          <table className="table table--fixed table--months">
            <thead>
              <tr>
                <th className="table__control">
                  <Check
                    checked={allChosen}
                    indeterminate={count > 0 && !allChosen}
                    onChange={months.chooseShown}
                    title={shownTitle}
                  />
                </th>
                <th>Счёт</th>
                <th>Месяц</th>
                <th>Записано в портфеле</th>
                <th>В файле</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => (
                <MonthRow
                  key={item.id}
                  item={item}
                  checked={chosen.includes(item.id)}
                  onToggle={() => months.toggle(item.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
        {hidden > 0 ? (
          <div className="table-more">
            <span className="muted">
              Показано {visible.length} из {items.length}. Непоказанные строки кнопки ниже не трогают.
            </span>
            <div className="row">
              <button type="button" className="button button--quiet" onClick={() => months.showMore(PAGE)}>
                Показать ещё {Math.min(PAGE, hidden)}
              </button>
              {hidden > PAGE ? (
                <button type="button" className="button button--quiet" onClick={() => months.showMore(hidden)}>
                  Показать все ({items.length})
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      <p className="muted">
        Отмечено {count} из показанных {visible.length}. Решение записывается в журнал действий с именем
        файла и суммами до и после.
      </p>
      {/* Решение - запись: при неоплаченной лицензии кнопки гаснут (строка 291 реестра). */}
      <Lock>
        <div className="row">
          <button type="button" className="button" disabled={idle} onClick={() => months.decide('keep')}>
            Оставить записанные ({count})
          </button>
          <button
            type="button"
            className="button button--quiet"
            disabled={idle || blocked}
            title={blocked ? BLOCKED_HINT : undefined}
            onClick={() => setConfirming(true)}
          >
            Заменить начислениями файла ({count})
          </button>
          <button type="button" className="button button--quiet" disabled={idle} onClick={() => months.decide('add')}>
            Дописать к записанным ({count})
          </button>
        </div>
      </Lock>
      {blocked ? <p className="muted">{BLOCKED_HINT}</p> : null}
      <p className="muted months__hint">
        «Дописать» - если в файле другая услуга, а не та же другими словами.
      </p>
      <TransferLock locked={locked} />
      {confirming ? null : <Notice error={error} />}
      {confirming ? (
        <Confirm
          title="Заменить записанные начисления?"
          action={`Заменить (${count})`}
          busy={busy}
          error={error}
          onCancel={closeConfirm}
          onConfirm={replace}
        >
          Записанные в портфеле начисления отмеченных месяцев ({count}) будут удалены, вместо них
          запишутся строки файла. Это не отменить.
          <TransferLock locked={locked} />
        </Confirm>
      ) : null}
    </section>
  )
}
